/**
 * Utility functions for handling authentication edge cases
 * particularly when users are redirected back from login
 */

import { logger } from "@/utils/logger";

/**
 * Retry a fetch request with authentication, handling the case where
 * auth cookies might not be fully propagated after login redirect
 */
export async function fetchWithAuthRetry(
  url: string,
  options: RequestInit = {},
  maxRetries: number = 2,
  retryDelay: number = 1000
): Promise<Response> {
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: "include", // Always include cookies
        cache: "no-store",
      });

      lastResponse = response;

      // If successful or not an auth error, return immediately
      if (response.ok || response.status !== 401) {
        return response;
      }

      // If it's an auth error and we have more retries, wait and try again
      if (attempt < maxRetries) {
        logger.debug("Auth failed, retrying", {
          component: "AuthHelpers",
          metadata: {
            attempt: attempt + 1,
            retryDelay,
          },
        });
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        // Increase delay for subsequent retries
        retryDelay *= 1.5;
      }
    } catch (error) {
      // Cancellation (including a caller-owned timeout) is terminal. Retrying
      // an already-aborted signal only adds the backoff delay and can leave a
      // React Query restore spinner waiting long after the request is gone.
      if (
        options.signal?.aborted ||
        (error instanceof Error && error.name === "AbortError")
      ) {
        throw error;
      }
      logger.error(
        `[fetchWithAuthRetry] Fetch error on attempt ${attempt + 1}:`,
        { component: "AuthHelpers" },
        error instanceof Error ? error : new Error(String(error))
      );
      if (attempt === maxRetries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      retryDelay *= 1.5;
    }
  }

  // If we get here, all retries failed
  return lastResponse!;
}

/**
 * Check if authentication is working by making a simple API call
 */
export async function verifyAuth(
  maxRetries: number = 2,
  retryDelay: number = 500
): Promise<boolean> {
  try {
    const response = await fetchWithAuthRetry(
      "/api/credits",
      {},
      maxRetries,
      retryDelay
    );
    return response.ok;
  } catch (error) {
    logger.error(
      "[verifyAuth] Auth verification failed:",
      { component: "AuthHelpers" },
      error instanceof Error ? error : new Error(String(error))
    );
    return false;
  }
}

/**
 * Wait for authentication to be ready after login redirect
 */
export async function waitForAuth(
  maxWaitTime: number = 5000,
  checkInterval: number = 500
): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitTime) {
    if (await verifyAuth(1, 0)) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }

  return false;
}
